import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { GithubIcon } from './Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';
import '../../styles/components/LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      setTimeout(() => firstFocusRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      login(email);
      onClose();
      setEmail('');
      setPassword('');
    }
  };

  const handleGuestLogin = () => {
    login('guest@faminiplus.com');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal__backdrop" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="login-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('login.title')}
        onClick={e => e.stopPropagation()}
      >
        <button className="login-modal__close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>

        <h2 className="login-modal__title">{t('login.title')}</h2>

        <form className="login-modal__form" onSubmit={handleSubmit}>
          <div className="login-modal__field">
            <input
              ref={firstFocusRef}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder=" "
              required
              className="login-modal__input"
              id="login-email"
            />
            <label htmlFor="login-email" className="login-modal__label">{t('login.email')}</label>
          </div>

          <div className="login-modal__field">
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder=" "
              required
              className="login-modal__input"
              id="login-password"
            />
            <label htmlFor="login-password" className="login-modal__label">{t('login.password')}</label>
          </div>

          <Button type="submit" variant="primary" size="lg" className="login-modal__submit">
            {t('login.submit')}
          </Button>
        </form>

        <div className="login-modal__divider">
          <span>{t('login.or')}</span>
        </div>

        <div className="login-modal__social">
          <button className="login-modal__social-btn" type="button" aria-label="Login with Google">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('login.google')}
          </button>
          <button className="login-modal__social-btn" type="button" aria-label="Login with GitHub">
            <GithubIcon size={20} />
            {t('login.github')}
          </button>
        </div>

        <button className="login-modal__guest" onClick={handleGuestLogin} type="button">
          {t('login.guest')}
        </button>
      </div>
    </div>
  );
};

export default LoginModal;
